import { VOICE_SCOPE_IGNORED_MESSAGE, VOICE_SCOPE_REJECT_GENERIC_MESSAGE } from './videoToolboxActivityLogLib';

export type VoiceScopeDismissedPresentation = {
  lead: string;
  body: string;
};

const voiceScopeRejectLeadPrefix = "Didn't respond to this instruction — ";

/** Maps a voice-rejected reason into the v2g-act dismissed row labels. */
export const parseVoiceScopeDismissedPresentation = (reason: string): VoiceScopeDismissedPresentation => {
  if (reason === VOICE_SCOPE_IGNORED_MESSAGE) {
    return {
      lead: 'Ignored',
      body: 'The Kai Agent only responds to task scoping instructions',
    };
  }

  if (reason === 'Proposal dismissed') {
    return { lead: 'Dismissed', body: reason };
  }

  if (reason.startsWith(voiceScopeRejectLeadPrefix)) {
    const detail = reason.slice(voiceScopeRejectLeadPrefix.length);
    return {
      lead: "Didn't respond",
      body: detail.charAt(0).toUpperCase() + detail.slice(1),
    };
  }

  if (reason === VOICE_SCOPE_REJECT_GENERIC_MESSAGE) {
    return {
      lead: "Didn't respond",
      body: "Couldn't apply the change",
    };
  }

  return { lead: 'Dismissed', body: reason };
};

export type VoiceScopeActPath = {
  where?: string;
  what: string;
  how?: string;
};

export type VoiceScopeActPresentation = {
  verb: 'Add' | 'Set';
  utype: 'new' | 'upd';
  pendingLead: string;
  appliedLead: string;
  path: VoiceScopeActPath;
};

/** Maps a voice-scope command summary into the v2g-act path and lead labels. */
export const parseVoiceScopeActPresentation = (summary: string): VoiceScopeActPresentation => {
  const addMatch = /^Add:\s*(.+)$/.exec(summary.trim());
  if (addMatch) {
    const segments = addMatch[1].split(' › ').map(part => part.trim()).filter(Boolean);
    const [what, ...rest] = segments;
    return {
      verb: 'Add',
      utype: 'new',
      pendingLead: 'Add this task?',
      appliedLead: 'Task added',
      path: {
        what: what ?? summary,
        how: rest.length > 0 ? rest.join(' › ') : undefined,
      },
    };
  }

  const updateMatch = /^Update:\s*([^:]+):\s*(.+)$/.exec(summary.trim());
  if (updateMatch) {
    return {
      verb: 'Set',
      utype: 'upd',
      pendingLead: 'Set this field?',
      appliedLead: 'Field set',
      path: {
        what: updateMatch[1].trim(),
        how: updateMatch[2].trim(),
      },
    };
  }

  return {
    verb: 'Add',
    utype: 'new',
    pendingLead: 'Apply this?',
    appliedLead: 'Applied',
    path: { what: summary },
  };
};
